'use client';

/*
  MaintenanceModeWatcher

  The `maintenance_mode_bop_ui` gate is evaluated in the locale layout, which only
  re-runs on a full page load. Client-side navigations would otherwise let a
  session started before the switch was flipped keep going indefinitely. Instead we poll 
  the server and force the layout to re-render once the switch is on.
*/

import { useRouter } from 'next/navigation';
import { useEffect, useRef } from 'react';

const POLL_INTERVAL_MS = 60_000;
// Spreads polls from tabs that were all opened at the same time.
const POLL_JITTER_MS = 15_000;

export function MaintenanceModeWatcher({
  children,
}: {
  readonly children?: React.ReactNode;
}) {
  const router = useRouter();

  const isRefreshing = useRef(false);

  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout>;
    const controller = new AbortController();

    const scheduleNextPoll = () => {
      timeoutId = setTimeout(
        poll,
        POLL_INTERVAL_MS + Math.random() * POLL_JITTER_MS,
      );
    };

    async function poll() {
      // Nothing can enter here after cleanup: it clears the pending timer and
      // removes the visibility listener, so only an in-flight fetch can outlive
      // the effect, and the signal below catches that.

      // A backgrounded tab does not need to know until the user returns to it.
      if (globalThis.document.visibilityState === 'hidden') {
        scheduleNextPoll();
        return;
      }

      try {
        const response = await fetch('/api/service-status', {
          cache: 'no-store',
          signal: controller.signal,
        });
        const { maintenanceMode } = (await response.json()) as {
          maintenanceMode?: boolean;
        };

        if (maintenanceMode) {
          if (!isRefreshing.current) {
            isRefreshing.current = true;
            router.refresh();
          }
          // The layout short-circuits to the maintenance-mode page on re-render,
          // so there is nothing left to poll for.
          return;
        }
      } catch {
        // A failed poll must never take down a working app, so keep polling.
      }

      if (!controller.signal.aborted) {
        scheduleNextPoll();
      }
    }

    function onVisibilityChange() {
      if (globalThis.document.visibilityState === 'visible') {
        clearTimeout(timeoutId);
        void poll();
      }
    }

    scheduleNextPoll();
    globalThis.document.addEventListener(
      'visibilitychange',
      onVisibilityChange,
    );

    return () => {
      controller.abort();
      clearTimeout(timeoutId);
      globalThis.document.removeEventListener(
        'visibilitychange',
        onVisibilityChange,
      );
    };
  }, [router]);

  return <>{children}</>;
}
